import useSWR from "swr";
import axios from "axios";

export interface IStakeInfo {
  id: string;
  accountAddress: string;
  stakeAmount: string;
  stakeDuration: string;
  unlockEpoch: number;
  stakeEpoch: number;
  unstakableAmount: string;
}

interface IStakeInfoResponse {
  data: {
    stakeInfos: IStakeInfo[];
  };
}

const SUBGRAPH_URL =
  "https://api.goldsky.com/api/public/project_clch40o0v0d510huoey7g5yaz/subgraphs/aevo-staking-v3/1.0.0/gn";

// Epoch 1 starts from 1765180800 (seconds), Monday 08:00 UTC
// Convert to milliseconds for comparison with Date.now()
const EPOCH_1_START_MS = 1765180800 * 1000;
const EPOCH_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 1 week in milliseconds

// Calculate current epoch number based on current time
export const getCurrentEpoch = (currentTime: number = Date.now()): number => {
  const timeDiff = currentTime - EPOCH_1_START_MS;
  if (timeDiff < 0) return 1;
  const epochNumber = Math.floor(timeDiff / EPOCH_DURATION_MS) + 1;
  return epochNumber;
};

// Calculate epoch number from a timestamp
export const getEpochFromTimestamp = (timestamp: number): number =>
  getCurrentEpoch(timestamp);

const fetchStakeInfo = async (
  epoch?: number
): Promise<IStakeInfo[] | undefined> => {
  try {
    const SUBGRAPH_LIMIT = 1000; // Subgraph default limit
    let allStakeInfos: IStakeInfo[] = [];
    let skip = 0;
    let hasMore = true;

    // Fetch all records using pagination
    // Sequential requests are required for pagination (each depends on previous result)
    /* eslint-disable no-await-in-loop */
    while (hasMore) {
      // Build GraphQL query with pagination
      // For a specific epoch, we want all stakes that are active during that epoch:
      // - stakeEpoch <= epoch (stake was created on or before this epoch)
      // - unlockEpoch > epoch (stake hasn't unlocked yet)
      const query = epoch
        ? `
          {
            stakeInfos(
              first: ${SUBGRAPH_LIMIT}
              skip: ${skip}
              where: {
                stakeEpoch_lte: ${epoch}
                unlockEpoch_gt: ${epoch}
              }
              orderBy: id
              orderDirection: asc
            ) {
              id
              accountAddress
              stakeAmount
              stakeDuration
              unlockEpoch
              stakeEpoch
              unstakableAmount
            }
          }
        `
        : `
          {
            stakeInfos(
              first: ${SUBGRAPH_LIMIT}
              skip: ${skip}
              orderBy: id
              orderDirection: asc
            ) {
              id
              accountAddress
              stakeAmount
              stakeDuration
              unlockEpoch
              stakeEpoch
              unstakableAmount
            }
          }
        `;

      const response = await axios.post<IStakeInfoResponse>(
        SUBGRAPH_URL,
        {
          query,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const { stakeInfos } = response.data.data;

      if (stakeInfos && stakeInfos.length > 0) {
        allStakeInfos = [...allStakeInfos, ...stakeInfos];

        // If we got fewer than the limit, we've fetched all records
        if (stakeInfos.length < SUBGRAPH_LIMIT) {
          hasMore = false;
        } else {
          skip += SUBGRAPH_LIMIT;
        }
      } else {
        hasMore = false;
      }
    }
    /* eslint-enable no-await-in-loop */

    // eslint-disable-next-line no-console
    console.log(
      `[StakeInfo] Found ${allStakeInfos.length} total stake accounts${
        epoch ? ` for epoch ${epoch}` : ""
      }`
    );

    return allStakeInfos;
  } catch (error) {
    // do nothing
    return undefined;
  }
};

export const useStakeInfo = (epoch?: number) => {
  const swr = useSWR<IStakeInfo[] | undefined>(
    epoch ? ["stake-info", epoch] : "stake-info",
    () => fetchStakeInfo(epoch),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 60000, // Cache for 1 minute
      refreshInterval: 30000, // Refresh every 30 seconds
    }
  );

  return {
    ...swr,
  };
};
